import { AddressElement } from '@stripe/react-stripe-js';
import { StripeAddressElementChangeEvent, StripeAddressElementOptions } from '@stripe/stripe-js';
import { useMemo, useRef } from 'react';
import { formatAddressLine, ShippingAddress } from '../../../lib/checkout/address';
import styles from './CheckoutStep.module.scss';

export interface ShipToCardProps {
  address: ShippingAddress;
  /** `complete` is Stripe's own validation of the address, including postal-code format */
  onChange: (address: ShippingAddress, complete: boolean) => void;
  /** Fired once, the first time the shopper changes what registration prefilled. */
  onCorrected: () => void;
}

type StripeAddressValue = StripeAddressElementChangeEvent['value'];

/** The card's DOM id, so a Confirm tapped with the address incomplete can bring it into view. */
export const SHIP_TO_CARD_ID = 'nano-checkout-ship-to';

/**
 * Maps Stripe's address value onto ours.
 *
 * Everything is trimmed: an autocomplete selection or a paste can carry leading and trailing space,
 * and Recurly rejects the whole billing address when a field is whitespace rather than empty.
 *
 * `display: { name: 'split' }` gives first and last name separately, but the split fields are only
 * populated once the shopper has typed in them -- an autocomplete selection can arrive with just the
 * combined `name`. So fall back to splitting it rather than saving an empty first name, which
 * `stripeCheckoutEndpoint` rejects outright.
 */
function toShippingAddress(value: StripeAddressValue, previous: ShippingAddress): ShippingAddress {
  const clean = (input: string | null | undefined) => (input ?? '').trim();
  const [firstFromCombined, ...restFromCombined] = clean(value.name).split(/\s+/);
  return {
    firstName: clean(value.firstName) || firstFromCombined || previous.firstName,
    lastName: clean(value.lastName) || restFromCombined.join(' ') || previous.lastName,
    line1: clean(value.address.line1),
    line2: clean(value.address.line2),
    city: clean(value.address.city),
    state: clean(value.address.state),
    zip: clean(value.address.postal_code),
    country: clean(value.address.country) || 'US',
    phone: clean(value.phone) || previous.phone,
  };
}

/**
 * The shipping address, prefilled from registration.
 *
 * Uses Stripe's `AddressElement` with the same options the shop's checkout uses, so the field layout
 * and the type-ahead address autocomplete are identical rather than a nano-specific reimplementation.
 *
 * The element stays mounted rather than collapsing to a summary once registration supplies an
 * address. Registration validates none of those fields, and a collapsed summary is never seen by
 * Stripe, so an address that is merely non-empty reached the purchase and failed there instead.
 */
export default function ShipToCard({ address, onChange, onCorrected }: ShipToCardProps) {
  // What registration captured. Frozen, because `defaultValues` only applies on mount and the parent
  // re-renders this card on every keystroke -- rebuilding the options would call `update` each time.
  const seeded = useRef(address);
  // Stripe's normalised view of the prefill, which is the baseline a correction is measured against.
  // Comparing against `seeded` instead would report a correction for Stripe's own tidying.
  const baseline = useRef<string | null>(null);
  const reportedCorrection = useRef(false);

  const options = useMemo<StripeAddressElementOptions>(
    () => ({
      mode: 'shipping',
      autocomplete: { mode: 'automatic' },
      display: { name: 'split' },
      fields: { phone: 'always' },
      validation: { phone: { required: 'always' } },
      // registration hardcodes US and the plan this flow sells has no non-USD price
      allowedCountries: ['US'],
      defaultValues: {
        firstName: seeded.current.firstName,
        lastName: seeded.current.lastName,
        phone: seeded.current.phone,
        address: {
          line1: seeded.current.line1,
          line2: seeded.current.line2,
          city: seeded.current.city,
          state: seeded.current.state,
          postal_code: seeded.current.zip,
          country: seeded.current.country || 'US',
        },
      },
    }),
    [],
  );

  const report = (value: StripeAddressValue, complete: boolean) => {
    const next = toShippingAddress(value, seeded.current);
    const line = formatAddressLine(next);
    if (baseline.current === null) {
      baseline.current = line;
    } else if (!reportedCorrection.current && line !== baseline.current) {
      reportedCorrection.current = true;
      onCorrected();
    }
    onChange(next, complete);
  };

  return (
    <div className={`${styles.card} ${styles.scrollTarget}`} id={SHIP_TO_CARD_ID}>
      <div className={styles.cardHeader}>
        <span className={styles.sectionTitle}>Ship to</span>
      </div>

      <AddressElement
        options={options}
        // `change` only fires once the shopper touches a field, so a prefilled address would never be
        // reported as usable and the confirm button would stay blocked. `getValue` asks Stripe to
        // validate what it was seeded with, which is the point of mounting it for a prefill at all.
        onReady={(element) => {
          void element.getValue().then(({ complete, value }) => report(value, complete));
        }}
        onChange={(event) => report(event.value, event.complete)}
      />
    </div>
  );
}
